import { ReactNode, useId, useMemo, useState } from 'react';
import { HelpCircle } from 'lucide-react';

interface MetricHelpTooltipProps {
  description: ReactNode;
  title?: ReactNode;
  className?: string;
  iconClassName?: string;
  ariaLabel?: string;
}

export function MetricHelpTooltip({
  description,
  title,
  className = '',
  iconClassName = '',
  ariaLabel,
}: MetricHelpTooltipProps) {
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const tooltipId = useId();

  const computedAriaLabel = useMemo(() => {
    if (ariaLabel) {
      return ariaLabel;
    }

    if (typeof title === 'string') {
      return title;
    }

    if (typeof description === 'string') {
      return description;
    }

    return 'Metric description';
  }, [ariaLabel, title, description]);

  return (
    <div
      className={`group relative inline-flex ${className}`}
      onMouseLeave={() => setIsTooltipVisible(false)}
    >
      <button
        type="button"
        aria-label={computedAriaLabel}
        aria-describedby={isTooltipVisible ? tooltipId : undefined}
        aria-expanded={isTooltipVisible}
        className={`inline-flex cursor-help items-center justify-center rounded-full p-1 text-[var(--anl-text-muted)] transition-colors hover:text-[var(--anl-text)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--anl-border)] ${iconClassName}`}
        onMouseEnter={() => setIsTooltipVisible(true)}
        onFocus={() => setIsTooltipVisible(true)}
        onBlur={() => setIsTooltipVisible(false)}
      >
        <HelpCircle className="h-4 w-4" aria-hidden="true" />
      </button>

      {isTooltipVisible && (
        <div className="absolute right-0 top-0 z-50 flex -translate-y-full translate-x-0 flex-col items-end">
          <div
            id={tooltipId}
            className="pointer-events-auto mb-2 mt-[-0.35rem] min-w-[14rem] max-w-xs rounded-md border border-[var(--anl-border)] bg-[var(--anl-surface-elev)] p-3 text-sm leading-snug text-[var(--anl-text)] shadow-lg"
            role="tooltip"
          >
            {title ? (
              <div className="mb-2 text-[0.7rem] font-semibold uppercase tracking-wide text-[var(--anl-text-muted)]">
                {title}
              </div>
            ) : null}
            <div>{description}</div>
          </div>
          <div
            className="pointer-events-none h-0 w-0 border-l-4 border-r-4 border-t-4 border-transparent"
            style={{ borderTopColor: 'var(--anl-surface-elev)' }}
          />
        </div>
      )}
    </div>
  );
}

export default MetricHelpTooltip;
